import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

interface FeatureFlags {
  rulesEngineEnabled: boolean;
  bulkOrderUploadEnabled: boolean;
  vehicleAssignmentMode: "none" | "default_only" | "per_order" | "default_with_override";
  defaultVehicleId: string | null;
}

export default function RulesFeatureSettings() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { data, isLoading } = useQuery<FeatureFlags>({
    queryKey: ["/api/admin/feature-flags"],
  });
  const { data: vehicles } = useQuery<any[]>({
    queryKey: ["/api/vehicles"],
  });

  const [flags, setFlags] = useState<FeatureFlags | null>(null);
  useEffect(() => { if (data) setFlags(data); }, [data]);

  const mutation = useMutation({
    mutationFn: async (payload: Partial<FeatureFlags>) =>
      apiRequest("PUT", "/api/admin/feature-flags", payload),
    onSuccess: () => {
      toast({ title: t("rules.settings.saved", "Settings saved") });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/feature-flags"] });
    },
    onError: (err: any) => toast({ variant: "destructive", title: "Error", description: err.message }),
  });

  if (isLoading || !flags) return <div className="p-6">{t("common.loading")}</div>;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6" data-testid="page-rules-feature-settings">
      <div>
        <h1 className="text-2xl font-bold">{t("rules.settings.title", "Workspace Feature Flags")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("rules.settings.subtitle", "Enable the Rules Engine, Bulk Order Upload, and vehicle assignment for this workspace.")}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("rules.settings.engineCard", "Order Rules Engine")}</CardTitle>
          <CardDescription>
            {t("rules.settings.engineDesc", "When enabled, configured rules are evaluated on every order create/edit and may block or warn.")}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-4">
          <Switch
            checked={flags.rulesEngineEnabled}
            onCheckedChange={(v) => setFlags({ ...flags, rulesEngineEnabled: v })}
            data-testid="switch-rules-enabled"
          />
          <Label>{flags.rulesEngineEnabled ? t("common.active") : t("common.inactive")}</Label>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("bulk_upload.settings.card", "Bulk Order Upload")}</CardTitle>
          <CardDescription>
            {t("bulk_upload.settings.desc", "When enabled, authorized users can upload XLSX/CSV files to create many orders at once.")}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-4">
          <Switch
            checked={flags.bulkOrderUploadEnabled}
            onCheckedChange={(v) => setFlags({ ...flags, bulkOrderUploadEnabled: v })}
            data-testid="switch-bulk-enabled"
          />
          <Label>{flags.bulkOrderUploadEnabled ? t("common.active") : t("common.inactive")}</Label>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("vehicles.assignment.card", "Vehicle Assignment Mode")}</CardTitle>
          <CardDescription>
            {t("vehicles.assignment.desc", "Controls whether orders may/must have a vehicle assigned.")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>{t("vehicles.assignment.modeLabel", "Mode")}</Label>
            <Select
              value={flags.vehicleAssignmentMode}
              onValueChange={(v: any) => setFlags({ ...flags, vehicleAssignmentMode: v })}
            >
              <SelectTrigger data-testid="select-vehicle-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("vehicles.assignment.none", "None — no vehicle on orders")}</SelectItem>
                <SelectItem value="default_only">{t("vehicles.assignment.defaultOnly", "Default only — auto-assigned")}</SelectItem>
                <SelectItem value="per_order">{t("vehicles.assignment.perOrder", "Per order — user picks each time")}</SelectItem>
                <SelectItem value="default_with_override">{t("vehicles.assignment.defaultOverride", "Default with override")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {flags.vehicleAssignmentMode !== "none" && flags.vehicleAssignmentMode !== "per_order" && (
            <div className="space-y-2">
              <Label>{t("vehicles.assignment.defaultLabel", "Default vehicle")}</Label>
              <Select
                value={flags.defaultVehicleId || ""}
                onValueChange={(v) => setFlags({ ...flags, defaultVehicleId: v || null })}
              >
                <SelectTrigger data-testid="select-default-vehicle"><SelectValue placeholder={t("common.none")} /></SelectTrigger>
                <SelectContent>
                  {(vehicles || []).map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.name} {v.plateNumber ? `(${v.plateNumber})` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => mutation.mutate(flags)} disabled={mutation.isPending} data-testid="button-save-flags">
          {mutation.isPending ? t("common.loading") : t("common.save")}
        </Button>
      </div>
    </div>
  );
}
